import { Link } from "react-router-dom"
import "./ServicePage.css"

const benefits = [
  {
    title: "Personalised Weight Management Plans",
    description: "Tailored programmes designed around your lifestyle, health conditions, and weight loss goals"
  },
  {
    title: "Prescription Weight Loss Medications",
    description: "Access to clinically proven treatments including GLP-1 medications where appropriate"
  },
  {
    title: "Regular Progress Monitoring",
    description: "Ongoing support with regular check-ins to track your progress and adjust your plan"
  },
  {
    title: "Expert Nutritional Guidance",
    description: "Practical advice on healthy eating habits and sustainable lifestyle changes"
  },
]

const eligibility = [
  {
    title: "Age Requirements",
    description: "Adults aged 18-75 years old"
  },
  {
    title: "BMI Assessment",
    description: "Generally suitable for adults with a BMI of 30 or above, or 27+ with weight-related health conditions"
  },
  {
    title: "Health Screening",
    description: "A thorough assessment to ensure any prescribed treatments are safe and appropriate for you"
  },
  {
    title: "Ongoing Commitment",
    description: "Willingness to engage with lifestyle modifications alongside any prescribed treatments"
  },
]

const pricing = [
  { dose: "2.5mg", price: "£165" },
  { dose: "5mg", price: "£180" },
  { dose: "7.5mg", price: "£230" },
  { dose: "10mg", price: "£260" },
  { dose: "12.5mg", price: "£280" },
  { dose: "15mg", price: "£300" },
]

const faqs = [
  {
    question: "What happens at my first consultation?",
    answer: "Your initial consultation includes a comprehensive health assessment, BMI calculation, discussion of your weight loss goals, medical history review, and an explanation of available treatment options. We'll work together to create a personalised plan that suits your needs."
  },
  {
    question: "What weight loss medications are available?",
    answer: "We offer clinically proven weight loss medications including GLP-1 receptor agonists such as Semaglutide and Tirzepatide where clinically appropriate. Our pharmacist will discuss which options may be suitable for you based on your individual circumstances."
  },
  {
    question: "How much does the service cost?",
    answer: "Our pricing starts from £165 for 2.5mg doses, with a £30 discount available for new clients on all doses. See our pricing section above for full details. Consultation fees are included in the medication price."
  },
  {
    question: "How often will I need appointments?",
    answer: "Appointments are scheduled every 4 weeks to monitor your progress and adjust your treatment if needed."
  },
  {
    question: "Are the treatments safe?",
    answer: "All medications we prescribe are licensed and clinically proven. Our pharmacist will conduct a thorough health assessment to ensure any treatment is safe for you and will monitor you throughout your weight loss journey."
  },
  {
    question: "What results can I expect?",
    answer: "Results vary between individuals. With a combination of medication (where appropriate) and lifestyle changes, many patients achieve clinically significant weight loss of 5-15% of their body weight over several months."
  },
]

function WeightLossClinicPage() {
  return (
    <div className="service-page">
      <section className="sp-hero" style={{ background: "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)" }}>
        <h1>Weight Loss Service</h1>
        <p className="sp-tagline">
          Professional support for your weight management journey
        </p>
      </section>

      <section className="sp-intro">
        <p>
          Our Weight Loss Service offers comprehensive, medically-supervised weight management
          services to help you achieve and maintain a healthy weight. Our qualified pharmacists
          provide personalised support, including access to prescription weight loss medications
          where clinically appropriate.
        </p>
        <p>
          We understand that weight management is a personal journey, and we're here to provide
          professional, confidential support every step of the way.
        </p>
      </section>

      <section className="sp-benefits">
        <h2>Our Services</h2>
        <div className="benefits-grid">
          {benefits.map((benefit, index) => (
            <div key={index} className="benefit-card">
              <div className="benefit-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                  <polyline points="22 4 12 14.01 9 11.01" />
                </svg>
              </div>
              <h3>{benefit.title}</h3>
              <p>{benefit.description}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="sp-benefits">
        <h2>Who Is This Service For?</h2>
        <div className="benefits-grid">
          {eligibility.map((item, index) => (
            <div key={index} className="benefit-card">
              <div className="benefit-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
                  <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
              </div>
              <h3>{item.title}</h3>
              <p>{item.description}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="sp-pricing">
        <h2>Mounjaro Pricing</h2>
        <p className="pricing-subtitle">Tirzepatide injection — price includes consultation and medication</p>
        <div className="pricing-table">
          <div className="pricing-header">
            <span>Dose</span>
            <span>Price</span>
          </div>
          {pricing.map((item, index) => (
            <div key={index} className="pricing-row">
              <span className="pricing-dose">{item.dose}</span>
              <span className="pricing-price">{item.price}</span>
            </div>
          ))}
        </div>
        <p className="pricing-discount-note">£30 discount available on all doses for new clients</p>
      </section>

      <section className="sp-cta">
        <h2>Start Your Weight Loss Journey</h2>
        <p>
          Take the first step towards a healthier you. Book a confidential consultation
          with our pharmacist to discuss your weight management options.
        </p>
        <Link to="/services?service=weight-loss" className="button primary">
          Book Consultation
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M9 18l6-6-6-6" />
          </svg>
        </Link>
      </section>

      <section className="sp-faq">
        <h2>Frequently Asked Questions</h2>
        <div className="faq-list">
          {faqs.map((faq, index) => (
            <div key={index} className="faq-item">
              <h3>{faq.question}</h3>
              <p>{faq.answer}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="sp-legal">
        <h2>Important Information</h2>
        <div className="legal-content">
          <h3>Medical Disclaimer</h3>
          <p>
            The weight loss services provided by Goosnargh Pharmacy are intended for adults aged 18 to 75.
            All treatments are subject to clinical suitability assessment by our qualified pharmacists.
            Weight loss medications are prescription-only medicines and will only be supplied following
            a thorough consultation and where clinically appropriate.
          </p>

          <h3>Eligibility Criteria</h3>
          <p>
            Weight loss medications are generally only prescribed for patients with a BMI of 30 kg/m² or above,
            or 27 kg/m² or above with at least one weight-related comorbidity (such as type 2 diabetes,
            hypertension, or dyslipidaemia). Individual eligibility will be determined during your consultation.
          </p>

          <h3>Treatment Expectations</h3>
          <p>
            Weight loss results vary between individuals and depend on multiple factors including adherence
            to treatment, lifestyle modifications, and individual metabolism. Medications should be used
            alongside a reduced-calorie diet and increased physical activity. There is no guarantee of
            specific weight loss outcomes.
          </p>

          <h3>Side Effects and Risks</h3>
          <p>
            Like all medications, weight loss treatments may cause side effects. Common side effects of
            GLP-1 medications include nausea, vomiting, diarrhoea, and constipation. Our pharmacist will
            discuss potential side effects and contraindications during your consultation. You should
            report any concerning symptoms immediately.
          </p>

          <h3>Regulatory Compliance</h3>
          <p>
            All weight loss medications supplied by Goosnargh Pharmacy are licensed by the Medicines and
            Healthcare products Regulatory Agency (MHRA) for use in the United Kingdom. Our pharmacy is
            registered with the General Pharmaceutical Council (GPhC) and operates in accordance with
            all applicable regulations and professional standards.
          </p>

          <h3>Data Protection</h3>
          <p>
            Your personal and medical information will be processed in accordance with the UK General Data
            Protection Regulation (UK GDPR) and the Data Protection Act 2018. Information shared during
            consultations is confidential and will only be used for the purpose of providing your healthcare
            services. For more information, please refer to our Privacy Policy.
          </p>

          <h3>Cancellation Policy</h3>
          <p>
            We require at least 24 hours' notice for appointment cancellations. Failure to attend without
            notice may result in a cancellation fee. Please contact us as soon as possible if you need
            to reschedule your appointment.
          </p>
        </div>
      </section>
    </div>
  )
}

export default WeightLossClinicPage
